import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { Calendar } from './components/ui/calendar';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import { 
  Palmtree, Sun, Waves, Wifi, Wind, UtensilsCrossed, MapPin, 
  Calendar as CalendarIcon, Users, Bed, Bath, ChevronRight,
  Menu, X, LogOut, User, LayoutDashboard, FileText, Home,
  DollarSign, CheckCircle, Clock, XCircle, Send
} from 'lucide-react';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd6b4759-2294-4d34-aa74-4441d033a3f6/artifacts/hqqncgjb_image.png";

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (sessionId) {
        try {
          const response = await fetch(`${API}/auth/session?session_id=${sessionId}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            toast.success(`Welcome, ${userData.name}!`);
            navigate('/dashboard', { replace: true, state: { user: userData } });
          } else {
            toast.error('Authentication failed');
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          toast.error('Authentication error');
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { user, login, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'glass shadow-lg' : 'bg-black/20 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Diamond Point Cottages" 
              className="h-20 w-auto drop-shadow-lg" 
              style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.8))' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center">
            <div className="flex items-center gap-2 bg-amber-500/80 backdrop-blur-sm rounded-full px-6 py-2">
              <Link to="/" className="text-white hover:text-slate-900 transition-colors font-bold text-lg px-3 py-1" data-testid="nav-home">
                Home
              </Link>
              <Link to="/accommodations" className="text-white hover:text-slate-900 transition-colors font-bold text-lg px-3 py-1" data-testid="nav-accommodations">
                Accommodations
              </Link>
              <a href="#location" className="text-white hover:text-slate-900 transition-colors font-bold text-lg px-3 py-1">
                Location
              </a>
              <a href="#restaurant" className="text-white hover:text-slate-900 transition-colors font-bold text-lg px-3 py-1">
                Restaurant
              </a>
              <a href="#contact" className="text-white hover:text-slate-900 transition-colors font-bold text-lg px-3 py-1">
                Contact
              </a>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span>{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={login} className="btn-primary" data-testid="login-btn">
                Sign In with Google
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/accommodations" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>Accommodations</Link>
              <a href="#location" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>Location</a>
              <a href="#contact" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <Separator />
              {user ? (
                <>
                  <Link to="/dashboard" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" className="block py-2 text-slate-700" onClick={() => setMobileMenuOpen(false)}>Admin Panel</Link>
                  )}
                  <button onClick={logout} className="block py-2 text-red-600">Logout</button>
                </>
              ) : (
                <Button onClick={login} className="w-full btn-primary">Sign In with Google</Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16" id="contact">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img 
            src={LOGO_URL} 
            alt="Diamond Point Cottages" 
            className="h-24 w-auto mb-6" 
            style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.5))' }}
          />
          <p className="text-slate-400 leading-relaxed max-w-md mb-6">
            Experience the best of Antigua and Barbuda at Diamond Point Cottages. 
            Your tropical paradise awaits with stunning beaches, crystal-clear waters, 
            and warm Caribbean hospitality.
          </p>
          <div className="flex gap-4">
            <a 
              href="https://wa.me/12687285086" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-3 text-slate-400">
            <li><Link to="/" className="hover:text-amber-400 transition-colors font-medium">Home</Link></li>
            <li><Link to="/accommodations" className="hover:text-amber-400 transition-colors font-medium">Accommodations</Link></li>
            <li><a href="#location" className="hover:text-amber-400 transition-colors font-medium">Location</a></li>
            <li><a href="#restaurant" className="hover:text-amber-400 transition-colors font-medium">D'Seagrape Restaurant</a></li>
            <li><a href="#contact" className="hover:text-amber-400 transition-colors font-medium">Contact</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-lg font-semibold mb-4">Contact Us</h4>
          <ul className="space-y-3 text-slate-400">
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              Diamond Point, Antigua and Barbuda
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              <a href="tel:+12687285086" className="hover:text-amber-400 transition-colors">+1 (268) 728-5086</a>
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              <a href="mailto:diamondpointcottages@gmail.com" className="hover:text-amber-400 transition-colors">diamondpointcottages@gmail.com</a>
            </li>
            <li className="flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <a href="https://ohbz.com/qr/2226a507-aab4-48a4-a4cc-02b7755525a4" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">D'Seagrape Restaurant & Bar</a>
            </li>
          </ul>
        </div>
      </div>
      <Separator className="my-8 bg-slate-700" />
      <div className="text-center text-slate-500 text-sm">
        © {new Date().getFullYear()} Diamond Point Cottages. All rights reserved.
      </div>
    </div>
  </footer>
);

// Home Page
const HomePage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [cottages, setCottages] = useState([]);

  useEffect(() => {
    const fetchCottages = async () => {
      try {
        const response = await fetch(`${API}/cottages`);
        const data = await response.json();
        setCottages(data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching cottages:', error);
      }
    };
    fetchCottages();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1641243150607-ad4de8fbc610?crop=entropy&cs=srgb&fm=jpg&q=85')` 
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-accent text-3xl md:text-4xl text-amber-400 mb-4"
          >
            Welcome to Paradise
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6"
          >
            Diamond Point Cottages
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed"
          >
            Experience the best beach bar restaurant in Antigua and Barbuda. 
            Luxurious cottages, pristine beaches, and unforgettable Caribbean hospitality.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              onClick={() => navigate('/accommodations')} 
              className="btn-secondary text-lg px-8 py-4"
              data-testid="hero-book-btn"
            >
              Book Now
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/accommodations')}
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-4 rounded-full"
              data-testid="hero-explore-btn"
            >
              Explore Cottages
            </Button>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronRight className="w-8 h-8 text-white rotate-90" />
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-2xl text-amber-500 mb-2">Why Choose Us</p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900">
              Caribbean Excellence
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Palmtree, title: "Tropical Setting", desc: "Nestled among palm trees with stunning ocean views" },
              { icon: UtensilsCrossed, title: "Beach Bar & Restaurant", desc: "Savor local cuisine and refreshing cocktails" },
              { icon: Waves, title: "Pristine Beaches", desc: "Steps away from crystal-clear Caribbean waters" }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 rounded-xl bg-slate-50 hover:shadow-lg transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-100 text-cyan-600 mb-6">
                  <feature.icon className="w-8 h-8" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Cottages */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="font-accent text-2xl text-amber-500 mb-2">Our Cottages</p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900">
                Find Your Perfect Stay
              </h2>
            </div>
            <Link 
              to="/accommodations" 
              className="hidden md:flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium"
              data-testid="view-all-cottages-link"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cottages.map((cottage, index) => (
              <motion.div
                key={cottage.cottage_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="cottage-card bg-white rounded-xl overflow-hidden shadow-md"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={cottage.featured_image} 
                    alt={cottage.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <Badge className="absolute top-4 right-4 bg-amber-500 text-white">
                    ${cottage.price_per_night}/night
                  </Badge>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2">{cottage.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{cottage.short_description}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" /> {cottage.max_guests}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bed className="w-4 h-4" /> {cottage.bedrooms}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bath className="w-4 h-4" /> {cottage.bathrooms}
                    </span>
                  </div>
                  <Button 
                    onClick={() => navigate(`/cottage/${cottage.cottage_id}`)}
                    className="w-full btn-primary"
                    data-testid={`view-cottage-${cottage.cottage_id}`}
                  >
                    View Details
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="md:hidden text-center mt-8">
            <Link 
              to="/accommodations"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium"
            >
              View All Cottages <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-white" id="location">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-accent text-2xl text-amber-500 mb-2">Find Us</p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                Your Caribbean Escape
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Located in the beautiful Antigua and Barbuda, Diamond Point Cottages offers 
                the perfect blend of tropical beauty and modern comfort. Our beachfront location 
                provides easy access to pristine beaches, local attractions, and authentic 
                Caribbean experiences.
              </p>
              <div className="space-y-4">
                {[
                  { icon: MapPin, text: "Diamond Point, Antigua and Barbuda" },
                  { icon: Waves, text: "Direct beach access" },
                  { icon: UtensilsCrossed, text: "On-site beach bar & restaurant" }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-cyan-600" />
                    </div>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3862.5!2d-61.897286!3d17.051941!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8c0c2c4e5c5c5c5c%3A0x348e8f4c8e4b618!2sDiamond%20Point%20Cottages!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Diamond Point Cottages Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Section */}
      <section className="py-20 bg-slate-50" id="restaurant">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                  alt="D'Seagrape Restaurant"
                  className="w-full h-96 object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="font-accent text-2xl text-amber-500 mb-2">Dine With Us</p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                D'Seagrape Restaurant & Bar
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Experience authentic Caribbean cuisine at D'Seagrape Restaurant & Bar - A Dennis Beach Bar Experience. 
                Enjoy fresh seafood, local specialties, and refreshing cocktails with your toes in the sand 
                and stunning ocean views.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: UtensilsCrossed, text: "Fresh Caribbean Cuisine" },
                  { icon: Waves, text: "Beachfront Dining" },
                  { icon: Sun, text: "Sunset Cocktails" }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              <a 
                href="https://ohbz.com/qr/2226a507-aab4-48a4-a4cc-02b7755525a4" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 btn-secondary text-lg"
                data-testid="restaurant-menu-btn"
              >
                <UtensilsCrossed className="w-5 h-5" />
                View Menu & Order
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-cyan-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <p className="font-accent text-2xl text-amber-400 mb-2">Ready for Paradise?</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            Book Your Caribbean Getaway Today
          </h2>
          <p className="text-cyan-100 text-lg mb-8">
            Minimum 3-night stay. Secure your spot in paradise with our easy online booking.
          </p>
          <Button 
            onClick={() => user ? navigate('/accommodations') : login()}
            className="btn-secondary text-lg px-10 py-4"
            data-testid="cta-book-btn"
          >
            {user ? 'Browse Cottages' : 'Sign In to Book'}
          </Button>
        </div>
      </section>
    </div>
  );
};

// Accommodations Page
const AccommodationsPage = () => {
  const [cottages, setCottages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCottages = async () => {
      try {
        const response = await fetch(`${API}/cottages`);
        const data = await response.json();
        setCottages(data);
      } catch (error) {
        console.error('Error fetching cottages:', error);
        toast.error('Failed to load cottages');
      } finally {
        setLoading(false);
      }
    };
    fetchCottages();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="font-accent text-2xl text-amber-500 mb-2">Our Accommodations</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Find Your Perfect Cottage
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Choose from our selection of beautifully appointed cottages, each offering unique charm 
            and all the comforts you need for an unforgettable Caribbean stay.
          </p>
        </div>

        {/* Tetris Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {cottages.map((cottage, index) => {
            // Tetris layout pattern
            const gridClasses = index === 0 
              ? "md:col-span-8 md:row-span-2" 
              : index === 1 
                ? "md:col-span-4 md:row-span-2"
                : "md:col-span-4";

            return (
              <motion.div
                key={cottage.cottage_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`cottage-card bg-white rounded-xl overflow-hidden shadow-md cursor-pointer ${gridClasses}`}
                onClick={() => navigate(`/cottage/${cottage.cottage_id}`)}
                data-testid={`cottage-card-${cottage.cottage_id}`}
              >
                <div className={`relative overflow-hidden ${index < 2 ? 'h-64 md:h-full' : 'aspect-video'}`}>
                  <img 
                    src={cottage.featured_image} 
                    alt={cottage.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <Badge className="mb-2 bg-amber-500 border-0">
                      ${cottage.price_per_night}/night
                    </Badge>
                    <h3 className="font-heading text-2xl font-semibold mb-1">{cottage.name}</h3>
                    <p className="text-slate-200 text-sm mb-3">{cottage.short_description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {cottage.max_guests} guests
                      </span>
                      <span className="flex items-center gap-1">
                        <Bed className="w-4 h-4" /> {cottage.bedrooms} bed
                      </span>
                      <span className="flex items-center gap-1">
                        <Bath className="w-4 h-4" /> {cottage.bathrooms} bath
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Cottage Detail Page
const CottageDetailPage = () => {
  const { cottageId } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [cottage, setCottage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState({ from: null, to: null });
  const [bookedDates, setBookedDates] = useState([]);
  const [guests, setGuests] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    const fetchCottage = async () => {
      try {
        const response = await fetch(`${API}/cottages/${cottageId}`);
        if (!response.ok) throw new Error('Cottage not found');
        const data = await response.json();
        setCottage(data);
      } catch (error) {
        console.error('Error fetching cottage:', error);
        toast.error('Cottage not found');
        navigate('/accommodations');
      } finally {
        setLoading(false);
      }
    };
    
    const fetchAvailability = async () => {
      try {
        const response = await fetch(`${API}/cottages/${cottageId}/availability`);
        const data = await response.json();
        setBookedDates(data.booked_dates.map(d => new Date(d)));
      } catch (error) {
        console.error('Error fetching availability:', error);
      }
    };

    fetchCottage();
    fetchAvailability();
  }, [cottageId, navigate]);

  const handleBooking = async () => {
    if (!user) {
      login();
      return;
    }

    if (!selectedDates.from || !selectedDates.to) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    const nights = Math.ceil((selectedDates.to - selectedDates.from) / (1000 * 60 * 60 * 24));
    if (nights < 3) {
      toast.error('Minimum stay is 3 nights');
      return;
    }

    setBookingLoading(true);
    try {
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cottage_id: cottageId,
          check_in: selectedDates.from.toISOString(),
          check_out: selectedDates.to.toISOString(),
          guests: guests
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Booking failed');
      }

      const booking = await response.json();
      toast.success('Booking created! Proceed to payment.');
      navigate(`/booking/${booking.booking_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!cottage) return null;

  const nights = selectedDates.from && selectedDates.to 
    ? Math.ceil((selectedDates.to - selectedDates.from) / (1000 * 60 * 60 * 24))
    : 0;
  const totalPrice = nights * cottage.price_per_night;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link to="/" className="text-slate-500 hover:text-cyan-600">Home</Link>
          <span className="mx-2 text-slate-400">/</span>
          <Link to="/accommodations" className="text-slate-500 hover:text-cyan-600">Accommodations</Link>
          <span className="mx-2 text-slate-400">/</span>
          <span className="text-slate-900">{cottage.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="rounded-xl overflow-hidden">
              <img 
                src={cottage.featured_image} 
                alt={cottage.name}
                className="w-full h-96 object-cover"
              />
            </div>

            {/* Details */}
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{cottage.name}</h1>
              <div className="flex items-center gap-4 text-slate-600 mb-6">
                <span className="flex items-center gap-1">
                  <Users className="w-5 h-5" /> Up to {cottage.max_guests} guests
                </span>
                <span className="flex items-center gap-1">
                  <Bed className="w-5 h-5" /> {cottage.bedrooms} bedroom
                </span>
                <span className="flex items-center gap-1">
                  <Bath className="w-5 h-5" /> {cottage.bathrooms} bathroom
                </span>
              </div>

              <p className="text-slate-700 leading-relaxed mb-8">{cottage.description}</p>

              <h3 className="font-heading text-xl font-semibold mb-4">Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {cottage.amenities.map((amenity) => (
                  <div key={amenity} className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-28">
              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-slate-900">${cottage.price_per_night}</span>
                <span className="text-slate-600"> / night</span>
              </div>

              <Separator className="my-4" />

              <div className="space-y-4">
                <div>
                  <Label className="mb-2 block">Select Dates</Label>
                  <Calendar
                    mode="range"
                    selected={selectedDates}
                    onSelect={setSelectedDates}
                    disabled={(date) => 
                      date < new Date() || 
                      bookedDates.some(d => d.toDateString() === date.toDateString())
                    }
                    className="rounded-md border"
                    data-testid="booking-calendar"
                  />
                  <p className="text-xs text-slate-500 mt-2">Minimum 3 nights</p>
                </div>

                <div>
                  <Label className="mb-2 block">Guests</Label>
                  <Select value={guests.toString()} onValueChange={(v) => setGuests(parseInt(v))}>
                    <SelectTrigger data-testid="guests-select">
                      <SelectValue placeholder="Select guests" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(cottage.max_guests)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1} {i === 0 ? 'Guest' : 'Guests'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {nights > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>${cottage.price_per_night} x {nights} nights</span>
                      <span>${totalPrice}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Total</span>
                      <span>${totalPrice}</span>
                    </div>
                  </div>
                )}

                <Button 
                  onClick={handleBooking}
                  disabled={bookingLoading || nights < 3}
                  className="w-full btn-secondary py-4"
                  data-testid="book-now-btn"
                >
                  {bookingLoading ? 'Processing...' : user ? 'Book Now' : 'Sign In to Book'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Booking Payment Page
const BookingPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await fetch(`${API}/bookings/${bookingId}`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Booking not found');
        const data = await response.json();
        setBooking(data);
      } catch (error) {
        toast.error('Booking not found');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId, navigate]);

  const handlePayPalApprove = async (data, actions) => {
    try {
      // Create order first
      const createResponse = await fetch(`${API}/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ booking_id: bookingId })
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create payment');
      }

      const { approval_url } = await createResponse.json();
      window.location.href = approval_url;
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!booking) return null;

  const checkIn = new Date(booking.check_in);
  const checkOut = new Date(booking.check_out);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8 text-center">
          Complete Your Booking
        </h1>

        <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
          <h2 className="font-heading text-xl font-semibold mb-4">Booking Summary</h2>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-slate-600">Cottage</span>
              <span className="font-medium">{booking.cottage_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Check-in</span>
              <span className="font-medium">{checkIn.toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Check-out</span>
              <span className="font-medium">{checkOut.toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Guests</span>
              <span className="font-medium">{booking.guests}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Nights</span>
              <span className="font-medium">{booking.nights}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-slate-600">${booking.price_per_night} x {booking.nights} nights</span>
              <span className="font-medium">${booking.total_price}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="text-cyan-600">${booking.total_price}</span>
            </div>
          </div>
        </div>

        {booking.status === 'pending' || booking.status === 'confirmed' ? (
          <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-8 text-center">
            <CheckCircle className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-cyan-800 mb-2">
              Booking Request Received!
            </h2>
            <p className="text-cyan-700 mb-4">
              Thank you for your booking request. Our team will contact you shortly to confirm your reservation and arrange payment details.
            </p>
            <div className="bg-white rounded-lg p-4 mb-6 text-left">
              <p className="text-slate-600 text-sm mb-2"><strong>What happens next:</strong></p>
              <ul className="text-slate-600 text-sm space-y-1">
                <li>• We'll review your booking request</li>
                <li>• You'll receive a confirmation email</li>
                <li>• Payment instructions will be provided</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a 
                href="https://wa.me/12687285086" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Contact via WhatsApp
              </a>
              <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                View My Bookings
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-green-800 mb-2">
              Booking {booking.status === 'paid' ? 'Confirmed!' : 'Status: ' + booking.status}
            </h2>
            <p className="text-green-700">Your booking has been processed successfully.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-6 btn-primary">
              View My Bookings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Booking Success Page
const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center px-4">
        <div className="bg-white rounded-xl p-8 shadow-sm">
          <CheckCircle className="w-20 h-20 text-cyan-500 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
            Booking Request Received!
          </h1>
          <p className="text-slate-600 mb-8">
            Thank you for your booking request. Our team will contact you shortly 
            to confirm your reservation and arrange payment details for your stay at Diamond Point Cottages.
          </p>
          <div className="space-y-3">
            <a 
              href="https://wa.me/12687285086" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Contact via WhatsApp
            </a>
            <Button onClick={() => navigate('/dashboard')} className="w-full btn-primary" data-testid="go-to-dashboard-btn">
              View My Bookings
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// User Dashboard
const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await fetch(`${API}/bookings/my`, {
          credentials: 'include'
        });
        const data = await response.json();
        setBookings(data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-slate-100 text-slate-800'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">My Dashboard</h1>
            <p className="text-slate-600">Welcome back, {user?.name}</p>
          </div>
          <Button onClick={() => navigate('/accommodations')} className="btn-primary" data-testid="new-booking-btn">
            New Booking
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="font-heading text-xl font-semibold">My Bookings</h2>
          </div>
          
          {bookings.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">No Bookings Yet</h3>
              <p className="text-slate-500 mb-6">Start planning your Caribbean getaway today!</p>
              <Button onClick={() => navigate('/accommodations')} className="btn-primary">
                Browse Cottages
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {bookings.map((booking) => (
                <div key={booking.booking_id} className="p-6 hover:bg-slate-50 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">{booking.cottage_name}</h3>
                      <p className="text-sm text-slate-600">
                        {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-slate-500">{booking.nights} nights • {booking.guests} guests</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">${booking.total_price}</p>
                        {getStatusBadge(booking.status)}
                      </div>
                      {(booking.status === 'pending' || booking.status === 'confirmed') && (
                        <Button 
                          onClick={() => navigate(`/booking/${booking.booking_id}`)}
                          variant="outline"
                          data-testid={`view-booking-${booking.booking_id}`}
                        >
                          View Details
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [statsRes, bookingsRes] = await Promise.all([
          fetch(`${API}/admin/stats`, { credentials: 'include' }),
          fetch(`${API}/admin/bookings`, { credentials: 'include' })
        ]);
        
        const statsData = await statsRes.json();
        const bookingsData = await bookingsRes.json();
        
        setStats(statsData);
        setBookings(bookingsData);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, navigate]);

  const handleSendInvoice = async (bookingId) => {
    setSendingInvoice(bookingId);
    try {
      const response = await fetch(`${API}/admin/invoices/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ booking_id: bookingId })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Invoice sent to ${data.email}`);
      } else {
        throw new Error('Failed to send invoice');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingInvoice(null);
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/status?status=${status}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        setBookings(bookings.map(b => 
          b.booking_id === bookingId ? { ...b, status } : b
        ));
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4 text-yellow-500" />,
      confirmed: <CheckCircle className="w-4 h-4 text-blue-500" />,
      paid: <DollarSign className="w-4 h-4 text-green-500" />,
      cancelled: <XCircle className="w-4 h-4 text-red-500" />,
      completed: <CheckCircle className="w-4 h-4 text-slate-500" />
    };
    return icons[status];
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">Admin Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Bookings', value: stats?.total_bookings, icon: CalendarIcon, color: 'bg-cyan-500' },
            { label: 'Pending', value: stats?.pending_bookings, icon: Clock, color: 'bg-yellow-500' },
            { label: 'Paid', value: stats?.paid_bookings, icon: CheckCircle, color: 'bg-green-500' },
            { label: 'Revenue', value: `$${stats?.total_revenue?.toLocaleString()}`, icon: DollarSign, color: 'bg-amber-500' }
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bookings Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="font-heading text-xl font-semibold">All Bookings</h2>
            <Badge variant="outline">{bookings.length} total</Badge>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-bookings-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Guest</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cottage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {bookings.map((booking) => (
                  <tr key={booking.booking_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-slate-900">{booking.user_name}</p>
                        <p className="text-sm text-slate-500">{booking.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">{booking.cottage_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-900">${booking.total_price}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(booking.status)}
                        <Select 
                          value={booking.status} 
                          onValueChange={(v) => handleUpdateStatus(booking.booking_id, v)}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendInvoice(booking.booking_id)}
                        disabled={sendingInvoice === booking.booking_id}
                        data-testid={`send-invoice-${booking.booking_id}`}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        {sendingInvoice === booking.booking_id ? 'Sending...' : 'Invoice'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// Router with hook support
import { useParams, useSearchParams } from 'react-router-dom';

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navigation />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/accommodations" element={<AccommodationsPage />} />
        <Route path="/cottage/:cottageId" element={<CottageDetailPage />} />
        <Route path="/booking/:bookingId" element={
          <ProtectedRoute>
            <BookingPage />
          </ProtectedRoute>
        } />
        <Route path="/booking/success" element={<BookingSuccessPage />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        } />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
